/**
 * NotepadPanel — right-side "Notepad": multiple named rich-text notes per
 * script. Writers keep working notes; feedback collaborators write their
 * feedback as notes of their own. Owner/co-writers can edit everything,
 * feedback authors edit their own notes, viewers read.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { X, Plus, ArrowLeft, Trash2, Maximize2, NotebookPen } from "lucide-react";
import { NoteDocEditor } from "@/components/editor/NotepadDoc";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function excerpt(html) {
  return (html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim().slice(0, 90);
}

export function NotepadPanel({ scriptId, canCreate, canModerate, currentUserId, onFullScreen, onClose, embedded = false, initialSelectedId = null }) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState(null);
  const [selectedId, setSelectedId] = useState(initialSelectedId);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/notepad`);
      setNotes(data.notes || []);
    } catch { setNotes([]); }
  }, [scriptId]);
  useEffect(() => { load(); }, [load]);

  const selected = notes?.find((n) => n.id === selectedId);
  const canEditNote = (n) => canModerate || n.author_id === currentUserId;

  const createNote = async () => {
    try {
      const { data } = await api.post(`/scripts/${scriptId}/notepad`, { title: "Untitled note" });
      setNotes((prev) => [data.note, ...(prev || [])]);
      setSelectedId(data.note.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the note");
    }
  };

  const removeNote = async (n) => {
    const ok = await confirm({ title: `Delete "${n.title || "Untitled note"}"?`, confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/scripts/${scriptId}/notepad/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      setSelectedId(null);
    } catch { toast.error("Couldn't delete the note"); }
  };

  const onSaved = useCallback((note) => {
    setNotes((prev) => (prev || []).map((x) => (x.id === note.id ? note : x)));
  }, []);

  const inner = selected ? (
    <NoteDocEditor
      key={selected.id}
      scriptId={scriptId}
      note={selected}
      canEdit={canEditNote(selected)}
      compact
      onSaved={onSaved}
      leftSlot={
        <button onClick={() => setSelectedId(null)} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)] shrink-0" title="All notes" data-testid="note-back-btn">
          <ArrowLeft size={14} strokeWidth={1.75} />
        </button>
      }
      rightSlot={
        <div className="flex items-center gap-0.5 shrink-0">
          {onFullScreen && (
            <button onClick={() => onFullScreen(selected.id)} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" title="Full screen — the note takes the stage, the script moves here" data-testid="note-fullscreen-btn">
              <Maximize2 size={14} strokeWidth={1.75} />
            </button>
          )}
          {canEditNote(selected) && (
            <button onClick={() => removeNote(selected)} className="p-1 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] hover:text-[var(--err)]" title="Delete note" data-testid="note-delete-btn">
              <Trash2 size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>
      }
    />
  ) : (
    <div className="flex flex-col h-full min-h-0" data-testid="notepad-list-view">
      {canCreate && (
        <div className="p-3 shrink-0">
          <button
            onClick={createNote}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] hover:text-[var(--ink-primary)] transition-colors"
            data-testid="note-create-btn"
          >
            <Plus size={14} strokeWidth={1.5} /> New note
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-2">
        {notes === null && <p className="text-sm text-[var(--ink-muted)] px-1 py-2">Loading notes…</p>}
        {notes && notes.length === 0 && (
          <p className="text-sm text-[var(--ink-muted)] px-1 py-2" data-testid="notepad-empty">
            {canCreate ? "A blank pad. Ideas, beats, research, feedback — start a note." : "No notes yet."}
          </p>
        )}
        {(notes || []).map((n) => (
          <button
            key={n.id}
            onClick={() => setSelectedId(n.id)}
            className="w-full text-left bg-white border border-[var(--line)] rounded-lg p-3 hover:border-[var(--brand)]/60 transition-colors"
            data-testid={`note-row-${n.id}`}
          >
            <div className="text-sm font-medium text-[var(--ink-primary)] truncate">{n.title || "Untitled note"}</div>
            {excerpt(n.content) && <p className="text-[11px] text-[var(--ink-secondary)] mt-0.5 line-clamp-2">{excerpt(n.content)}</p>}
            <div className="flex items-center gap-2 text-[10px] text-[var(--ink-muted)] mt-1.5">
              <span>{n.author_name || "Someone"}</span>
              <span>·</span>
              <span>{timeAgo(n.updated_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  if (embedded) {
    return <div className="flex flex-col h-full min-h-0" data-testid="notepad-panel">{inner}</div>;
  }

  return (
    <aside className="w-[320px] shrink-0 bg-[var(--sidebar-bg)] border-l border-[var(--line)] flex flex-col h-full" data-testid="notepad-panel">
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)] shrink-0">
        <span className="font-display text-base text-[var(--ink-primary)] flex items-center gap-2"><NotebookPen size={15} className="text-[var(--brand)]" /> Notepad</span>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="notepad-close-btn">
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
      {inner}
    </aside>
  );
}
